/**
 * Where a room starts on a phone and an iPad: "What are you making?", a recording's first step (the talk), and a live
 * room's two (when, and what it will cover). The same steps the computer has, from the same hooks, said one question
 * to a screen.
 */
import { type ReactNode, useRef, useState } from "react";
import { Link } from "react-router";
import { uploadLimitLabel } from "../../../shared/board-caps";
import { LIVE_LENGTHS, liveFileKind, notesEnough } from "../../../shared/live-entry";
import type { LibraryAsset } from "../../../shared/types";
import { bytesLabel, lenLabel } from "../../assets/library";
import { openAssets } from "../../assets/store";
import { useAuth } from "../../auth/AuthContext";
import { DaySheet } from "../../components/DaySheet";
import { GoLiveSheet } from "../../components/GoLiveSheet";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { PushedNav } from "../../components/season/PushedNav";
import { TimeZonePicker } from "../../components/TimeZonePicker";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import { Select } from "../../components/ui/Select";
import { SAMPLE_ART } from "../../lib/sample-art";
import { dayLabel, type LiveDraft, startTimeGroups, todayIn } from "../liveForm";
import { pickTalk, talkRefusal } from "../make/MakeSteps";
import { lengthLine, lengthWords, useLiveStart, useRecordingStart } from "../useStart";
import { RepeatPicker } from "../RepeatPicker";
import { SeasonMakeFrame } from "./SeasonMakeFrame";

const ROOMS = { to: "/rooms", label: "Rooms" };
const NEW_ROOM = "/rooms/new";

function Note({ children }: { children: ReactNode }) {
  return (
    <p className="note warn" role="alert">
      <Icon name="info" />
      <span>{children}</span>
    </p>
  );
}

/** "What are you making?": a recording or a live room, and Go live now. */
export function SeasonNewRoom({ layout }: { layout: SeasonLayout }) {
  const { user } = useAuth();
  const [goingLive, setGoingLive] = useState(false);
  const choice = (to: string, art: string, pill: ReactNode, title: string, line: string) => (
    <Link to={to} className="k-card k-flush mk-choice">
      <span className="k-fr k-fr-lg mk-cposter">
        <img src={art} alt="" />
        <span className="k-ov">{pill}</span>
      </span>
      <span className="mk-cb">
        <b>{title}</b>
        <span>{line}</span>
      </span>
      <Icon name="chev" />
    </Link>
  );
  return (
    <Page layout={layout} className="mk mk-pick">
      {layout === "phone" ? (
        <PushedNav back={ROOMS} />
      ) : (
        <div className="k-head">
          <div>
            <Link to={ROOMS.to} className="k-back">
              <Icon name="left" />
              <span>{ROOMS.label}</span>
            </Link>
          </div>
        </div>
      )}
      <h1 className="k-h1 mk-h1">What are you making?</h1>
      <div className="mk-choices">
        {choice(`${NEW_ROOM}?from=recording`, SAMPLE_ART.talk, <span className="pill wine">A recording</span>, "A recording", "A talk you have already recorded.")}
        {choice(`${NEW_ROOM}?from=live`, SAMPLE_ART.live, <span className="pill livep">A live room</span>, "A live room", "Live on a date, once or every week.")}
      </div>
      <GroupedList className="mk-after">
        <ListRow
          lead={
            <span className="k-ico">
              <i className="mk-ld" />
            </span>
          }
          title="Go live now"
          onClick={() => setGoingLive(true)}
        />
      </GroupedList>
      {goingLive ? <GoLiveSheet hostFirst={user?.first_name ?? ""} onClose={() => setGoingLive(false)} onMade={() => setGoingLive(false)} /> : null}
    </Page>
  );
}

/** A recording's step 1: the talk, then Make the room. `from` is a talk already chosen in Assets. */
export function SeasonRecordingStart({ layout, onStarted, from }: { layout: SeasonLayout; onStarted: (webinarId: string) => void; from?: string }) {
  const { starting, error, start } = useRecordingStart(onStarted, from);
  return (
    <SeasonTalkStep
      layout={layout}
      starting={starting}
      error={error}
      onMake={(talk) => start({ id: talk.id, name: talk.name, size: talk.size_bytes })}
      back={{ to: NEW_ROOM, label: "Back" }}
    />
  );
}

/** The talk step: Upload a talk or Record one, the chosen talk as a row with Change, and the docked Make the room. */
export function SeasonTalkStep({
  layout,
  starting,
  error,
  onMake,
  back,
  onFinishLater,
}: {
  layout: SeasonLayout;
  starting: boolean;
  error: string;
  onMake: (talk: LibraryAsset) => void;
  back: { to: string; label: string };
  onFinishLater?: () => void;
}) {
  const [talk, setTalk] = useState<LibraryAsset | null>(null);
  const [refused, setRefused] = useState("");
  const camera = useRef<HTMLInputElement>(null);
  const choose = () => pickTalk(setTalk, undefined, undefined, "Use this talk");
  // Why the camera app: the app records notes, not talks, so a phone records one with its own camera and the picker takes it.
  const recorded = (file: File | undefined) => {
    if (!file) return;
    const why = talkRefusal(file);
    setRefused(why);
    if (!why) pickTalk(setTalk, [file], undefined, "Use this talk");
  };
  const make = () => {
    if (talk && !starting) onMake(talk);
  };
  const sub = talk ? [talk.duration_sec != null ? lenLabel(talk.duration_sec) : "", talk.size_bytes ? bytesLabel(talk.size_bytes) : ""].filter(Boolean).join(" · ") : "";
  return (
    <SeasonMakeFrame
      layout={layout}
      kind="rec"
      current={0}
      reached={0}
      kicker="Step 1 of 7"
      title="Your talk"
      back={back}
      onFinishLater={onFinishLater}
      action={
        talk || starting ? (
          <Button variant="p" size="lg" full={layout === "phone"} after="right" disabled={starting} onClick={make}>
            {starting ? "Starting…" : "Make the room"}
          </Button>
        ) : null
      }
    >
      {talk ? (
        <GroupedList>
          <ListRow
            lead={<span className="mk-th">{talk.poster_url ? <img src={talk.poster_url} alt="" /> : <Icon name="video" />}</span>}
            title={talk.name}
            sub={sub}
            end={
              <button type="button" className="k-link" onClick={choose}>
                Change
              </button>
            }
          />
        </GroupedList>
      ) : (
        <div className="mk-drop">
          <span className="mk-dic">
            <Icon name="upload" />
          </span>
          <p>MP4 or MOV, up to {uploadLimitLabel()}</p>
          <Button variant="p" size="lg" icon="upload" disabled={starting} onClick={choose}>
            Upload a talk
          </Button>
          <Button size="lg" icon="video" disabled={starting} onClick={() => camera.current?.click()}>
            Record one
          </Button>
          <input
            ref={camera}
            type="file"
            accept="video/*"
            capture="user"
            hidden
            aria-label="Record a talk"
            onChange={(e) => {
              recorded(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </div>
      )}
      {refused || error ? <Note>{refused || error}</Note> : null}
    </SeasonMakeFrame>
  );
}

/** When: the day as words that open a date sheet, the start, how long, the zone, and whether it repeats. */
function SeasonWhen({ draft, onChange }: { draft: LiveDraft; onChange: (patch: Partial<LiveDraft>) => void }) {
  const [picking, setPicking] = useState(false);
  return (
    <div className="k-form mk-form">
      <div className="mk-g2">
        <div className="field">
          <span className="lbl">Day</span>
          <button type="button" className="inp mk-day" aria-label={`Day, ${draft.date ? dayLabel(draft.date) : "pick one"}`} onClick={() => setPicking(true)}>
            <Icon name="cal" />
            <span>{draft.date ? dayLabel(draft.date) : "Pick a day"}</span>
          </button>
        </div>
        <div className="field">
          <span className="lbl">Starts</span>
          <Select ariaLabel="Starts" placeholder="Pick a time" value={draft.time} suggested="09:00" options={startTimeGroups(draft.date, draft.timezone)} onChange={(time) => onChange({ time })} />
        </div>
        <div className="field">
          <span className="lbl">For</span>
          <Select ariaLabel="For" value={String(draft.lengthMin)} options={LIVE_LENGTHS.map((l) => ({ value: String(l.min), label: lengthWords(l.min) }))} onChange={(min) => onChange({ lengthMin: Number(min) })} />
        </div>
        <div className="field">
          <span className="lbl">Time zone</span>
          <TimeZonePicker className="nbzone" value={draft.timezone} onChange={(timezone) => onChange({ timezone })} />
        </div>
      </div>
      <div className="field">
        <span className="lbl">Repeats</span>
        <RepeatPicker
          when={{ date: draft.date, time: draft.time, timezone: draft.timezone }}
          value={draft.pattern}
          onChange={(pattern) => onChange({ pattern })}
        />
      </div>
      {picking ? <DaySheet value={draft.date} min={todayIn(draft.timezone)} onPick={(date) => onChange({ date })} onClose={() => setPicking(false)} /> : null}
    </div>
  );
}

/** A live room's two steps: when, and what it will cover. "Write the page" mints the draft. */
export function SeasonLiveStart({ layout, onStarted }: { layout: SeasonLayout; onStarted: (webinarId: string) => void }) {
  const s = useLiveStart(onStarted);
  const { draft, step, file } = s;
  const [tried, setTried] = useState(false);
  const phone = layout === "phone";
  const write = () => {
    if (!notesEnough(draft.notes)) {
      setTried(true);
      return;
    }
    void s.write();
  };
  if (step === 0) {
    return (
      <SeasonMakeFrame
        layout={layout}
        kind="live"
        current={0}
        reached={0}
        kicker="Step 1 of 4"
        title="When is it?"
        back={{ to: NEW_ROOM, label: "Back" }}
        action={
          <Button variant="p" size="lg" full={phone} after="right" disabled={!s.whenOk} onClick={() => s.setStep(1)}>
            Continue
          </Button>
        }
      >
        <SeasonWhen draft={draft} onChange={s.set} />
        {s.at ? (
          <div className="mk-sum" role={s.passed || s.problem ? "alert" : undefined}>
            <span className={`mk-sico${s.passed || s.problem ? " bad" : ""}`}>
              <Icon name={s.passed || s.problem ? "info" : "cal"} />
            </span>
            <div>
              <b>{s.passed ? "That time has already passed." : s.problem ? s.problem : s.whenLine}</b>
              <span>{s.passed || s.problem ? "Pick a later one." : lengthLine(draft)}</span>
            </div>
          </div>
        ) : null}
      </SeasonMakeFrame>
    );
  }
  const warn = !!s.refused || tried || s.short;
  return (
    <SeasonMakeFrame
      layout={layout}
      kind="live"
      current={1}
      reached={1}
      kicker="Step 2 of 4"
      title="What will you cover?"
      back={{ to: `${NEW_ROOM}?from=live`, label: "Back", onBack: () => s.setStep(0) }}
      action={
        <Button variant="p" size="lg" full={phone} icon="sparkle" disabled={s.starting} onClick={write}>
          {s.starting ? "Starting…" : "Write the page"}
        </Button>
      }
    >
      <div className="k-form mk-form">
        <textarea dir="auto"
          className="inp mk-notes"
          rows={6}
          aria-label="What will you cover?"
          value={draft.notes}
          onChange={(e) => s.set({ notes: e.target.value })}
          placeholder="For example: A live Q&A on the winter market. I’ll cover what sold this fall, whether to list now or wait for spring, and take your questions until the hour is up."
        />
        {file ? (
          <GroupedList>
            <ListRow
              lead={
                <span className="mk-th">
                  <Icon name="file" />
                </span>
              }
              title={file.name}
              sub={liveFileKind(file.name, file.mime) === "pdf" ? "PDF" : "Text"}
              end={
                <button type="button" className="k-ib" aria-label="Take the file off" onClick={() => s.setFile(null)}>
                  <Icon name="x" />
                </button>
              }
            />
          </GroupedList>
        ) : (
          <div>
            <Button icon="clip" onClick={() => openAssets({ fits: "notes", title: "Notes or slides", cta: "Use this file", onPick: s.take })}>
              Notes or slides
            </Button>
          </div>
        )}
        <p className={`mk-hint${warn ? " warn" : ""}`}>{s.refused || (warn ? "A few more words" : "PDF or text, up to 10 MB")}</p>
        {s.error ? <Note>{s.error}</Note> : null}
      </div>
    </SeasonMakeFrame>
  );
}
